import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button,
  Card,
  ConfirmationModal,
  EmptyState,
  Input,
  LoadingState,
  MobileSearchField,
  SectionHeader,
  Typography,
} from '../components/common';
import {
  fetchClubLeagueLinksByStatus,
  fetchLeaguePlayerSuppressions,
  reviewClubLeagueLink,
  searchLeaguePlayersForAdmin,
  suppressLeaguePlayer,
  unlinkClubLeagueLink,
  unsuppressLeaguePlayer,
} from '../services/leagueService';

const formatReviewedDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const playerContextCaption = (player) => {
  const parts = [];
  if (player.clubs?.length) parts.push(player.clubs.join(', '));
  if (player.teams?.length) parts.push(player.teams.join(', '));
  if (player.seasons?.length) parts.push(player.seasons.join(', '));
  return parts.join(' · ') || 'No match cards yet';
};

const actorName = (actor) =>
  actor ? `${actor.first_name ?? ''} ${actor.last_name ?? ''}`.trim() || 'Admin' : 'Admin';

const LeagueAdminPage = ({ currentUser }) => {
  const isSiteAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const [pendingRows, setPendingRows] = useState([]);
  const [approvedRows, setApprovedRows] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [playerQuery, setPlayerQuery] = useState('');
  const [searchHits, setSearchHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [suppressTarget, setSuppressTarget] = useState(null);
  const [suppressNote, setSuppressNote] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, approved, withheld] = await Promise.all([
        fetchClubLeagueLinksByStatus('pending'),
        fetchClubLeagueLinksByStatus('approved'),
        fetchLeaguePlayerSuppressions(),
      ]);
      setPendingRows(pending);
      setApprovedRows(approved);
      setSuppressions(withheld);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSiteAdmin) reload();
  }, [isSiteAdmin, reload]);

  useEffect(() => {
    const q = playerQuery.trim();
    if (q.length < 2) {
      setSearchHits([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      searchLeaguePlayersForAdmin(q)
        .then((rows) => {
          if (!cancelled) setSearchHits(rows);
        })
        .catch((err) => {
          if (!cancelled) {
            setSearchHits([]);
            setError(err.message);
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [playerQuery]);

  if (!isSiteAdmin) {
    return <Navigate to="/" replace />;
  }

  const review = async (linkId, approve) => {
    setBusyId(linkId);
    try {
      await reviewClubLeagueLink(linkId, approve);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget) return;
    try {
      await unlinkClubLeagueLink(unlinkTarget.id);
      setUnlinkTarget(null);
      await reload();
    } catch (err) {
      setError(err.message);
      setUnlinkTarget(null);
    }
  };

  const confirmSuppress = async () => {
    if (!suppressTarget) return;
    try {
      await suppressLeaguePlayer(suppressTarget.id, suppressNote.trim());
      setSuppressTarget(null);
      setSuppressNote('');
      setPlayerQuery('');
      setSearchHits([]);
      await reload();
    } catch (err) {
      setError(err.message);
      setSuppressTarget(null);
    }
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    try {
      await unsuppressLeaguePlayer(restoreTarget.id);
      setRestoreTarget(null);
      await reload();
    } catch (err) {
      setError(err.message);
      setRestoreTarget(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading league links…" />;
  }

  return (
    <div className="space-y-8">
      {error ? <Typography variant="warning">{error}</Typography> : null}

      <section className="space-y-4">
        <SectionHeader title="Pending requests" subtitle="Approve or reject county club claims" />
        {pendingRows.length === 0 ? (
          <EmptyState title="No pending requests" description="Club admins request links from the Club dashboard League tab." />
        ) : (
          <Card size="small" className="p-0 sm:p-0">
            <ul>
              {pendingRows.map((row) => (
                <li key={row.id} className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Typography variant="label">{row.club?.name ?? 'Club'}</Typography>
                    <Typography variant="caption">
                      wants {row.leagueClub?.name ?? 'county club'}
                    </Typography>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="small"
                      variant="primary"
                      disabled={busyId === row.id}
                      onClick={() => review(row.id, true)}
                    >
                      Approve
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      variant="ghost"
                      disabled={busyId === row.id}
                      onClick={() => review(row.id, false)}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      <section className="space-y-4">
        <SectionHeader title="Linked clubs" subtitle="Approved county club links" />
        {approvedRows.length === 0 ? (
          <EmptyState title="No linked clubs" description="Approved links appear here and can be removed." />
        ) : (
          <Card size="small" className="p-0 sm:p-0">
            <ul>
              {approvedRows.map((row) => (
                <li key={row.id} className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Typography variant="label">{row.club?.name ?? 'Club'}</Typography>
                    <Typography variant="caption">
                      linked to {row.leagueClub?.name ?? 'county club'}
                      {row.reviewed_at ? ` · approved ${formatReviewedDate(row.reviewed_at)}` : ''}
                    </Typography>
                  </div>
                  <Button
                    type="button"
                    size="small"
                    variant="outline"
                    aria-label={`Unlink ${row.club?.name ?? 'club'} from ${row.leagueClub?.name ?? 'county club'}`}
                    onClick={() => setUnlinkTarget(row)}
                  >
                    Unlink
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Removed league players"
          subtitle="People who emailed asking for their name to be withheld"
        />
        <MobileSearchField
          value={playerQuery}
          onChange={(event) => setPlayerQuery(event.target.value)}
          placeholder="Search by league name…"
          aria-label="Search league players to withhold"
          showIcon
        />
        {playerQuery.trim().length >= 2 ? (
          <Card size="small" className="p-0 sm:p-0">
            {searching && searchHits.length === 0 ? (
              <div className="px-4 py-3">
                <Typography variant="muted">Searching…</Typography>
              </div>
            ) : searchHits.length === 0 ? (
              <div className="px-4 py-3">
                <Typography variant="muted">No players match “{playerQuery.trim()}”.</Typography>
              </div>
            ) : (
              <ul>
                {searchHits.map((player) => (
                  <li key={player.id} className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <Typography variant="label" className="truncate">{player.display_name}</Typography>
                      <Typography variant="caption">{playerContextCaption(player)}</Typography>
                    </div>
                    <Button
                      type="button"
                      size="small"
                      variant="outline"
                      onClick={() => {
                        setSuppressNote('');
                        setSuppressTarget(player);
                      }}
                    >
                      Remove name
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {suppressions.length === 0 ? (
          <EmptyState
            title="No withheld names"
            description="When someone emails asking to be removed, search above and add them here."
          />
        ) : (
          <Card size="small" className="p-0 sm:p-0">
            <ul>
              {suppressions.map((row) => (
                <li key={row.id} className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Typography variant="label">MCBA player {row.source_player_id}</Typography>
                    <Typography variant="caption">
                      {[
                        row.note,
                        `added ${formatReviewedDate(row.created_at)}`,
                        actorName(row.createdBy),
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  </div>
                  <Button
                    type="button"
                    size="small"
                    variant="ghost"
                    onClick={() => setRestoreTarget(row)}
                  >
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      <ConfirmationModal
        isOpen={Boolean(unlinkTarget)}
        onClose={() => setUnlinkTarget(null)}
        onConfirm={confirmUnlink}
        title="Unlink county club?"
        message={
          unlinkTarget
            ? `${unlinkTarget.club?.name ?? 'This club'} will lose its League tab data for ${unlinkTarget.leagueClub?.name ?? 'the county club'}, and its player links will be removed. The club admin can request a new link.`
            : ''
        }
        confirmText="Unlink"
        isDangerous
      />

      <ConfirmationModal
        isOpen={Boolean(suppressTarget)}
        onClose={() => {
          setSuppressTarget(null);
          setSuppressNote('');
        }}
        onConfirm={confirmSuppress}
        title="Remove this name?"
        message={
          suppressTarget ? (
            <div className="space-y-3">
              <p>
                {suppressTarget.display_name} will show as Withheld player on all match cards, any Shuttlr
                link is removed, and future imports keep it hidden.
              </p>
              <label htmlFor="league-suppress-note" className="block text-xs font-medium text-text-muted">
                Note (optional)
              </label>
              <Input
                id="league-suppress-note"
                value={suppressNote}
                onChange={(event) => setSuppressNote(event.target.value)}
                placeholder="Emailed 2 Oct, Hit & Run"
              />
            </div>
          ) : null
        }
        confirmText="Remove name"
        isDangerous
      />

      <ConfirmationModal
        isOpen={Boolean(restoreTarget)}
        onClose={() => setRestoreTarget(null)}
        onConfirm={confirmRestore}
        title="Restore this name?"
        message="The real name returns on the next scrape. Until then, match cards still show Withheld player."
        confirmText="Restore"
      />
    </div>
  );
};

export default LeagueAdminPage;
